import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from './config';
import { User } from '../types';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

// Sign in with email and password
export const signIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

// Sign up with email and password
export const signUp = async (email: string, password: string, displayName?: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
    }
    
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error: any) {
    return { error: error.message };
  }
};

// Get current user
export const getCurrentUser = (): FirebaseUser | null => {
  return auth.currentUser;
};

// Listen to auth state changes
export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    const result = await signInWithPopup(auth, provider);
    return { user: result.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

// Check if user is authorized to access the system
export const checkUserAuthorization = async (email: string): Promise<{ authorized: boolean; user?: User; error?: string }> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const userDoc = await getDoc(doc(db, 'users', normalizedEmail));
    
    if (!userDoc.exists()) {
      return { authorized: false, error: 'User not found in system. Please contact your administrator.' };
    }
    
    const userData = userDoc.data() as User;
    
    if (!userData.isActive) {
      return { authorized: false, error: 'Your account has been deactivated. Please contact your administrator.' };
    }
    
    return { authorized: true, user: userData };
  } catch (error: any) {
    return { authorized: false, error: 'Error checking user authorization.' };
  }
};

// Create or update user with Google profile data (no role changes, no user creation)
export const createOrUpdateUserWithGoogleData = async (firebaseUser: FirebaseUser): Promise<{ success: boolean; error?: string }> => {
  try {
    const normalizedEmail = firebaseUser.email!.toLowerCase();
    // Only proceed if the user already exists; do not create new users here
    const existingUserDoc = await getDoc(doc(db, 'users', normalizedEmail));
    if (!existingUserDoc.exists()) {
      return { success: false, error: 'User not found. Please contact your administrator.' };
    }
    const existingUser = existingUserDoc.data() as User;

    // Only update non-privileged profile fields
    const profileUpdates: Partial<User> = {
      displayName: firebaseUser.displayName || existingUser.displayName,
      photoURL: firebaseUser.photoURL || existingUser.photoURL,
      updatedAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString()
    };

    await updateDoc(doc(db, 'users', normalizedEmail), profileUpdates);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Update user's last login time
export const updateUserLastLogin = async (email: string) => {
  try {
    const normalizedEmail = email.toLowerCase();
    await updateDoc(doc(db, 'users', normalizedEmail), {
      lastLoginAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
  }
};

// Get current user data from Firestore
export const getCurrentUserData = async (email: string): Promise<User | null> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const userDoc = await getDoc(doc(db, 'users', normalizedEmail));
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};
